import React, { useState } from 'react';
import { NotificationToast } from '../notification-toast';
import Label from '../../atoms/text/label';
import Text from '../../atoms/text';
import SVGIcon from '../../atoms/icons';

interface MultiImageUploadProps {
	files: File[];
	setFiles: React.Dispatch<React.SetStateAction<File[]>>;
}

const MultiImageUpload = ({ files, setFiles }: MultiImageUploadProps) => {
	const [images, setImages] = useState<string[]>([]);

	const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const files = event.target.files;
		if (files) handleImageUpload(files);
	};

	const handleImageUpload = (newFiles: FileList) => {
		const fileArray = Array.from(newFiles);
		const readers = fileArray.map((file) => {
			return new Promise<string | null>((resolve) => {
				// Verifica se o arquivo é uma imagem
				if (!file.type.startsWith('image/')) {
					NotificationToast.Warn(
						'Por favor, envie apenas arquivos de imagem.'
					);
					resolve(null);
				} else {
					const reader = new FileReader();
					reader.onload = () => resolve(reader.result as string);
					reader.readAsDataURL(file);
					setFiles((prev) => [...prev, file]);
				}
			});
		});

		// Filtra os arquivos que são imagens válidas e atualiza o estado
		Promise.all(readers).then((loadedImages) => {
			const validImages = loadedImages.filter(
				(image) => image !== null
			) as string[];
			setImages((prevImages) => [...prevImages, ...validImages]);
		});
	};

	const removeImage = (index: number) => {
		setImages((prevImages) => prevImages.filter((_, i) => i !== index));
		setFiles((prevImages) => prevImages.filter((_, i) => i !== index));
	};

	// Funções para arrastar e soltar
	const handleDragOver = (event: React.DragEvent<HTMLLabelElement>) => {
		event.preventDefault(); // Necessário para permitir o "drop"
	};

	const handleDrop = (event: React.DragEvent<HTMLLabelElement>) => {
		event.preventDefault(); // Impede o comportamento padrão
		const files = event.dataTransfer.files;
		if (files) handleImageUpload(files);
	};

	return (
		<div>
			<Label
				className="text-center flex flex-col items-center justify-center border-2 border-dashed border-gray-border rounded-md p-6 cursor-pointer transition-colors hover:border-primary group"
				htmlFor="multi-image-upload"
				onDrag={handleDragOver}
				onDrop={handleDrop}
			>
				<input
					className="hidden"
					id="multi-image-upload"
					type="file"
					accept="image/*"
					onChange={handleImageChange}
					multiple
				/>
				<SVGIcon
					className="h-8 w-auto fill-gray-label group-hover:fill-primary"
					icon="Upload"
				/>
				<Text className="group-hover:text-primary">
					Clique ou arraste para fazer upload
				</Text>
				<Text className="group-hover:text-primary text-xs">
					Formatos suportados: JPG, PNG
				</Text>
			</Label>

			{/* Exibe as imagens carregadas em um grid */}
			<div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
				{images.map((src, index) => (
					<div key={index} className="relative group">
						<img
							src={src}
							alt={`Uploaded ${index}`}
							className="w-full h-48 object-cover rounded-md"
						/>
						<div
							className="absolute top-0 right-0 rounded-full size-6 flex items-center justify-center cursor-pointer transition-colors bg-[#f5f5f580] hover:bg-gray-bg"
							onClick={() => removeImage(index)}
						>
							<SVGIcon
								className="fill-red h-4 w-auto"
								icon="Delete"
							/>
						</div>
					</div>
				))}
			</div>
		</div>
	);
};

export default MultiImageUpload;
